import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Edit2, Trash2, BookOpen, ChevronRight } from "lucide-react";
import { CONTENT } from "@/constants/testIds";
import { useDepartments } from "@/hooks/useDepartments";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

function formatDate(dt) {
  try { return new Date(dt).toLocaleDateString("pt-BR"); } catch { return dt; }
}

export default function ProceduresPage() {
  const { hasAnyRole, user } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const { departments } = useDepartments();
  const DEPTS = ["Todos", ...departments];
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("Todos");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", department: "", category: "", version: "1.0", tags: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    ax().get(`${API}/procedures`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(i => {
    const matchSearch = i.title?.toLowerCase().includes(search.toLowerCase()) || i.content?.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === "Todos" || i.department === deptFilter;
    return matchSearch && matchDept;
  });

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", department: user?.department || "", category: "", version: "1.0", tags: "" }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, department: item.department || "", category: item.category || "", version: item.version || "1.0", tags: (item.tags || []).join(", ") }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()) : [] };
    try {
      if (editing) await ax().put(`${API}/procedures/${editing.item_id}`, payload);
      else await ax().post(`${API}/procedures`, payload);
      setShowForm(false);
      load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este procedimento?")) return;
    await ax().delete(`${API}/procedures/${id}`);
    load();
    if (selected?.item_id === id) setSelected(null);
  };

  const depts = [...new Set(items.map(i => i.department).filter(Boolean))];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Procedimentos</h1>
            <p className="text-sm text-slate-500">{filtered.length} procedimentos</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Novo Procedimento
          </Button>
        )}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid={CONTENT.searchInput} placeholder="Buscar procedimentos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger data-testid={CONTENT.filterSelect} className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Department pills */}
      <div className="flex gap-2 flex-wrap mb-4">
        {["Todos", ...depts].map(d => (
          <button key={d} onClick={() => setDeptFilter(d)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${deptFilter === d ? "bg-[#2346ff] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#2346ff] hover:text-[#2346ff]"}`}>
            {d}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div data-testid={CONTENT.contentList} className="space-y-2">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum procedimento encontrado</p>
            </div>
          )}
          {filtered.map(item => (
            <div key={item.item_id} data-testid={CONTENT.contentItem} className="bento-card p-4 cursor-pointer group" onClick={() => setSelected(selected?.item_id === item.item_id ? null : item)}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-800 group-hover:text-[#2346ff] transition-colors">{item.title}</h3>
                    {item.department && <Badge className="text-xs" style={{ backgroundColor: "#e8ecff", color: "#2346ff" }}>{item.department}</Badge>}
                    {item.category && <Badge variant="secondary" className="text-xs">{item.category}</Badge>}
                    {item.version && <span className="text-xs text-slate-400 font-mono">v{item.version}</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Atualizado em {formatDate(item.updated_at)} · {item.created_by_name}</p>
                </div>
                <div className="flex items-center gap-1">
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selected?.item_id === item.item_id ? "rotate-90" : ""}`} />
                  {canEdit && (
                    <>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={e => { e.stopPropagation(); openEdit(item); }}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-red-500" onClick={e => { e.stopPropagation(); remove(item.item_id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
              {selected?.item_id === item.item_id && (
                <div data-testid={CONTENT.contentDetail} className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{item.content}</p>
                  {item.tags?.length > 0 && (
                    <div className="flex gap-1 mt-3 flex-wrap">
                      {item.tags.map(t => <span key={t} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">#{t}</span>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Procedimento" : "Novo Procedimento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Título do procedimento" /></div>
            <div><Label>Conteúdo *</Label><Textarea rows={6} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} placeholder="Descreva o procedimento passo a passo..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Departamento</Label>
                <Select value={form.department} onValueChange={v => setForm(f => ({...f, department: v}))}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{DEPTS.filter(d => d !== "Todos").map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Versão</Label><Input value={form.version} onChange={e => setForm(f => ({...f, version: e.target.value}))} placeholder="1.0" /></div>
            </div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="Ex: Operações de Loja" /></div>
            <div><Label>Tags (separadas por vírgula)</Label><Input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} placeholder="tag1, tag2" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.content} style={{ backgroundColor: "#2346ff" }} className="text-white">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
